import { Button } from '@mui/material';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useSearchFilter from '../../../../../hooks/useSearchFilter/useSearchFilter';
import { fetchClaimLinkTemplatesRequest } from '../../../../../store/actions/templates';
import { selectFilteredDepartmentListForUser } from '../../../../../store/selectors/organizations';
import {
  selectClaimLinkTemplatesIsLoading,
  selectClaimLinkTemplatesTotalCount,
  selectFilteredClaimLinkTemplatesForUser,
} from '../../../../../store/selectors/templates';
import { IFiltersRef } from '../../../../../types/tables';
import { ClaimLinkTemplateStatusEnum, IFetchClaimLinkTemplatesPayload } from '../../../../../types/templates';
import { ClaimLinkTemplateFilters } from '../../../../TemplatesComponent';
import { TableSearchInput as ExternalInput } from '../../../index';

interface IUseFiltersOptions {
  onSubmit: () => void;
  // Whether there's an effective template selection (picked in the modal or already saved).
  hasSelection: boolean;
  departmentId?: string;
  orgId?: string;
  showActionButton?: boolean;
}

export const useFilters = ({
  onSubmit,
  hasSelection,
  departmentId,
  orgId,
  showActionButton = true,
}: IUseFiltersOptions) => {
  const filtersRef = useRef<IFiltersRef>(null);

  const dispatch = useDispatch();
  const allDepartments = useSelector(selectFilteredDepartmentListForUser) ?? [];
  const claimLinkTemplates = useSelector(selectFilteredClaimLinkTemplatesForUser) || [];
  const isTemplatesFetching = useSelector(selectClaimLinkTemplatesIsLoading);
  const templateTotalCount = useSelector(selectClaimLinkTemplatesTotalCount) || 0;

  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  const { SearchInput, query, resetSearch } = useSearchFilter({
    items: [],
    fieldNames: [],
    ExternalInput,
    props: { dataAction: 'template_modal_search', disabled: false },
  });

  const filters = useMemo(
    () => ({
      query,
      selectedDepartments,
    }),
    [query, selectedDepartments],
  );

  const fetchClaimLinkTemplates = useCallback(
    (payload: Omit<IFetchClaimLinkTemplatesPayload, 'reject' | 'resolve'>) =>
      new Promise<void>((resolve, reject) => {
        const preparedQuery = query?.toLowerCase().trim();
        const departmentIds = departmentId ? [departmentId] : selectedDepartments;
        dispatch(
          fetchClaimLinkTemplatesRequest({
            ...(preparedQuery && { search_query: preparedQuery }),
            ...(departmentIds.length > 0 && { department_ids: departmentIds }),
            ...(orgId && { org_id: orgId }),
            ...payload,
            status: ClaimLinkTemplateStatusEnum.Live,
            resolve,
            reject,
          }),
        );
      }),
    [dispatch, query, selectedDepartments, departmentId, orgId],
  );

  const onClear = useCallback(() => {
    resetSearch();
    setSelectedDepartments([]);
  }, [resetSearch]);

  const hasFiltersApplied = Boolean(query || selectedDepartments.length);

  // The org genuinely has no templates only when the count is 0 AND no filter is narrowing the
  // results — a filtered-to-empty query still means templates exist. Done may be enabled without a
  // selection only in the truly-empty case; otherwise it requires an effective selection.
  const getSubmitDisabled = useCallback(
    (totalCount: number) => {
      const orgHasNoTemplates = totalCount === 0 && !hasFiltersApplied;

      return !orgHasNoTemplates && !hasSelection;
    },
    [hasFiltersApplied, hasSelection],
  );

  const isSubmitDisabled = getSubmitDisabled(templateTotalCount);

  const actionButton = useMemo(
    () =>
      showActionButton ? (
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          data-action="done_btn"
        >
          Done
        </Button>
      ) : undefined,
    [showActionButton, onSubmit, isSubmitDisabled],
  );

  const Component = useMemo(
    () => (
      <ClaimLinkTemplateFilters
        ref={filtersRef}
        renderSearchField={SearchInput}
        onSearchReset={resetSearch}
        query={query}
        selectedDepartments={selectedDepartments}
        onDepartmentsChange={setSelectedDepartments}
        filterConfig={[]}
        departments={departmentId ? [] : allDepartments}
        actionButton={actionButton}
      />
    ),
    [selectedDepartments, query, resetSearch, filtersRef, SearchInput, allDepartments, actionButton],
  );

  return {
    Filters: Component,
    fetchClaimLinkTemplates,
    claimLinkTemplates,
    isTemplatesFetching,
    templateTotalCount,
    filtersRef,
    onClear,
    filters,
    isSubmitDisabled,
    getSubmitDisabled,
  };
};
